/**
 * Exceptions — read-only list of decisions needing human attention.
 *
 * Reads GET /api/agentic/exceptions (backend/app/routes/agentic_governance.py):
 * escalated (max-hold), rejected, and low-confidence non-routine decisions.
 * Each row links to the Decision card. On 403/404 → NotEnabled.
 */
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ChevronRight, ShieldAlert } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  KpiCard,
  PageHeader,
  Section,
  Spinner,
  Stack,
  Text,
} from '@smms/design-system';
import { getExceptions, type ExceptionItem } from '../api/agentic';
import { isNotEntitled } from '../api/client';
import NotEnabled from '../components/NotEnabled';

const SEVERITY_BADGE: Record<string, 'danger' | 'warning' | 'neutral'> = {
  high: 'danger',
  medium: 'warning',
  low: 'neutral',
};

const CATEGORY_LABEL: Record<string, string> = {
  escalated: 'Escalated',
  rejected: 'Rejected',
  low_confidence: 'Low confidence',
};

function humanize(s: string): string {
  return s.replaceAll('_', ' ');
}

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function ExceptionsPage() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agentic', 'exceptions'],
    queryFn: getExceptions,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="exceptions-loading">
        <Spinner size="lg" label="Loading exceptions" />
      </div>
    );
  }

  if (error) {
    if (isNotEntitled(error)) return <NotEnabled />;
    return (
      <div className="flex min-h-[60vh] items-center justify-center p-8">
        <EmptyState
          variant="error"
          icon={<Icon as={AlertTriangle} size="lg" />}
          title="Couldn't load exceptions"
          description="Something went wrong while loading exceptions. Try again."
          action={<Button onClick={() => void refetch()}>Retry</Button>}
        />
      </div>
    );
  }

  if (!data) return <NotEnabled />;

  const { items, by_category } = data;

  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      <Stack gap="lg">
        <PageHeader
          title="Exceptions"
          description="Agent decisions that need human attention — escalations, rejections, and low-confidence calls. Read-only; open a row for the full decision."
        />

        <Section>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <KpiCard label="Escalated" value={by_category.escalated ?? 0} />
            <KpiCard label="Rejected" value={by_category.rejected ?? 0} />
            <KpiCard label="Low confidence" value={by_category.low_confidence ?? 0} />
          </div>
        </Section>

        <Section>
          {items.length === 0 ? (
            <Card padding="lg">
              <EmptyState
                compact
                icon={<Icon as={ShieldAlert} size="lg" />}
                title="No open exceptions"
                description="When agent decisions escalate, get rejected, or fall below the confidence bar, they surface here."
              />
            </Card>
          ) : (
            <Card padding="none">
              <ul className="divide-y divide-border" data-testid="exception-list">
                {items.map((x: ExceptionItem, i: number) => (
                  <li key={`${x.agent_decision_id}-${x.category}-${i}`}>
                    <Link
                      to={`/decisions/${x.agent_decision_id}`}
                      className="flex flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-elevated"
                    >
                      <Badge variant={SEVERITY_BADGE[x.severity] ?? 'neutral'} size="sm">
                        {CATEGORY_LABEL[x.category] ?? humanize(x.category)}
                      </Badge>
                      <div className="min-w-0 flex-1">
                        <Text variant="body-sm" className="font-medium">
                          {humanize(x.decision_type)}
                        </Text>
                        <Text variant="caption" tone="muted">
                          {humanize(x.agent_id)} · {x.detail} · {formatWhen(x.created_at)}
                        </Text>
                      </div>
                      <Icon as={ChevronRight} size="sm" className="text-muted" />
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </Section>
      </Stack>
    </div>
  );
}
